import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  downloadMapPdf,
  downloadMapXlsx,
  fetchMapHealth,
  mapDataQuery,
  mapScreenQuery,
  type MapOption,
  type MapQuery,
} from '../../api/admMap'
import { shellQuery } from '../../api/shell'
import { Content } from '../../ui/Shell'
import { Forbidden } from '../../ui/Forbidden'
import { usePageStyles } from '../../ui/usePageStyles'
import { AdmPageHead } from './AdmPageHead'
import { drawMap, mapPng, startPulse, HEALTH_DEGRADED, HEALTH_DOWN, type Cy } from './map/draw'
import { ARRANGES, ARRANGE_LABELS, isArrange, rearrangeMap, type Arrange, type ArrangeCy } from './map/layouts'
import { SettingsCheck } from '../../ui/SettingsParts'
import { DEFAULT_GROUP_STATE, buildView, readGroupState, toggleCollapsed, type MapGroupState } from './map/grouping'
import { MapControls } from './map/MapControls'
import { MapExportButton } from './map/MapExportButton'
import { readPositions, type Positions } from './map/positions'
import {
  SavedViewsBar,
  forgetLast,
  lastKey,
  mapScope,
  queryFromState,
  readLast,
  useSavedViews,
  viewScope,
  writeLast,
  type MapViewState,
  type Viewport,
} from './map/savedViews'

/**
 * ADM -> Map, din `views/version-3/softsee/adm/map.php` si `AdmMap` din
 * `public/js/adm.js`.
 *
 * LA CE RASPUNDE ECRANUL: „pe ce se sprijina lucrul asta, si ce cade odata cu
 * el". Se intra pe doua usi - din meniul Softsee, pe parcul intreg, si de pe
 * fisa unui lucru cu `?root=<id>`, din tichete, din registru si din catalogul
 * de servicii.
 *
 * FARA PANOU IN JURUL LUI. Celelalte ecrane Softsee isi pun continutul direct
 * in pagina, iar `panel-primary` picteaza o bara de titlu care nu seamana cu
 * nimic din modul.
 *
 * BARA DE FILTRE E UN RAND FLEX, cu eticheta deasupra fiecarui camp. Pe linia
 * de baza a textului, un select, o bifa si un buton ajung la trei inaltimi
 * diferite.
 *
 * INCREDEREA E ASCUNSA SI LASATA PE „TOT", ca in portal: era pusa implicit pe
 * 85%, si asta insemna ca harta ascundea de la sine legaturi pe care nu ceruse
 * nimeni sa fie ascunse - iar cine se uita la un desen incomplet n-avea de unde
 * sti ca e incomplet. O harta care minte tacut e mai rea decat una incarcata.
 *
 * TREI FORME PENTRU TREI DRUMURI ale aceluiasi graf: poza se lipeste intr-un
 * slide, documentul se semneaza si se claseaza, foaia de calcul se filtreaza si
 * se numara. Poza pleaca din browser - asezarea nodurilor traieste doar aici -
 * si tot ea e corpul documentului; foaia o scrie serverul din aceiasi parametri
 * ca desenul. Exportul ia TOT graful desenat, nu doar bucata din fereastra: o
 * poza dintr-un colt al hartii nu probeaza nimic la un audit.
 *
 * Butonul de PDF se deseneaza numai daca serverul chiar poate scoate unul
 * (`pdfAvailable`) - unul care nu face nimic e mai rau decat unul care lipseste.
 */

const STYLES = ['css/adm.css', 'css/adm-map-groups.css']

/** Din minut in minut, ca alarmele sa apara singure - `pollHealth()`. */
const HEALTH_EVERY_MS = 60000

/**
 * Ce tine bara: filtrele trimise serverului, plus `layout` - asezarea, care
 * NU pleaca la server. Se schimba pe panza deja desenata, fara alta cerere.
 */
type MapState = Omit<MapQuery, 'depth'> & { layout?: Arrange }

/**
 * ADANCIMEA E MEREU PLAFONUL SERVERULUI - `Map::MAX_DEPTH`, trimis ca
 * `maxDepth` in ecran. Controlul Depth nu se mai deseneaza (cerinta Roxanei,
 * 7 octombrie 2026): harta arata tot ce se leaga, iar ce o margineste cu
 * adevarat e bugetul de noduri. Se TRIMITE explicit, fiindca implicitul
 * serverului e 2, nu plafonul. Zece cat timp ecranul n-a sosit - dar harta
 * nici nu se cere pana atunci.
 *
 * Un `depth` stocat intr-un view salvat ramane in baza, dar nu se mai aplica
 * (`apply`, mai jos); un `?depth=` din adresa nu ajunge nici pana aici -
 * ruta citeste doar `root` si `view`.
 */
const MAP_MAX_DEPTH = 10

function rootLabel(item: MapOption): string {
  return item.className === '' ? item.name : `${item.name} — ${item.className}`
}

export function MapPage({ root = 0, view: requestedView = 0 }: { root?: number; view?: number }) {
  const shell = useQuery(shellQuery('softsee'))
  usePageStyles(STYLES, shell.data?.version ?? '')

  const screen = useQuery(mapScreenQuery(root))

  /* Vederea porneste pe „communication", ca in portal: intrebarea cu care vine
     lumea pe ecran e „ce vorbeste cu ce". */
  const [query, setQuery] = useState<MapState>({ root, view: 'communication', direction: 'both', show_gone: 0, layout: 'auto' })
  const { layout = 'auto', ...filters } = query
  const fetchQuery: MapQuery = { ...filters, depth: screen.data?.maxDepth ?? MAP_MAX_DEPTH }
  /* Citita din desen fara sa-l porneasca din nou: o asezare noua se aplica pe
     panza care exista (efectul de mai jos), nu o redeseneaza. */
  const layoutRef = useRef<Arrange>(layout)
  layoutRef.current = layout
  const [drawn, setDrawn] = useState<{ nodes: number; edges: number } | null>(null)
  const [exporting, setExporting] = useState('')
  const [exportError, setExportError] = useState('')
  const [redraws, setRedraws] = useState(0)
  /* Casetele si filtrele - pe client, fara cerere noua. Cheile sunt stabile:
     vederile salvate le scriu asa cum sunt (`map/grouping.ts`). */
  const [grouping, setGrouping] = useState<MapGroupState>(DEFAULT_GROUP_STATE)

  /*
   * VIEW-URILE SALVATE (`map/savedViews.tsx`). `layoutMemo` tine pozitiile si
   * zoomul view-ului deschis, plus ce a mutat omul peste el de atunci; fara
   * view, pozitiile vin din ultima stare a omului, din browser.
   */
  const layoutMemo = useRef<{ positions: Positions; viewport: Viewport | null } | null>(null)
  const [drawTick, setDrawTick] = useState(0)
  const userId = shell.data?.user?.id ?? 0
  const lastTimer = useRef(0)

  const savedViews = useSavedViews({
    requestedId: requestedView,
    autoOpenDefault: requestedView === 0 && root === 0,
    userId,
    userReady: !shell.isPending,
    /* Ce se salveaza si ce face „modified": filtrele, asezarea (`layout`, in
       query) si casetele (`grouping`, chei plate: group, collapsed, ...). */
    query: { ...query, ...grouping } as Record<string, unknown>,
    capture: (): MapViewState => {
      const cy = cyRef.current
      return {
        ...(query as Record<string, unknown>),
        ...(grouping as unknown as Record<string, unknown>),
        viewport: cy ? { zoom: cy.zoom(), pan: cy.pan() } : (layoutMemo.current?.viewport ?? null),
        positions: { ...(layoutMemo.current?.positions ?? {}), ...(cy ? readPositions(cy) : {}) },
      }
    },
    apply: (state) => {
      const restored = queryFromState(state)
      const nextGrouping = readGroupState(restored)
      for (const key of Object.keys(DEFAULT_GROUP_STATE)) delete restored[key]
      /* Adancimea salvata nu se mai aplica: mereu plafonul (`MAP_MAX_DEPTH`). */
      delete restored.depth
      const next = { ...query, ...restored } as MapState
      if (!isArrange(next.layout)) next.layout = 'auto'
      /* Asezarea view-ului e deja in pozitiile lui: nu se mai reaseaza peste ele. */
      arranged.current = next.layout
      setGrouping(nextGrouping)
      layoutMemo.current = { positions: state.positions ?? {}, viewport: state.viewport ?? null }
      moved.current = false
      viewport.current = null
      setQuery(next)
      setDrawTick((n) => n + 1)
      return { ...next, ...nextGrouping } as Record<string, unknown>
    },
  })
  const activeId = savedViews.active?.id ?? 0
  const scopeKey = lastKey(userId, activeId > 0 ? viewScope(activeId) : mapScope(query.root, query.view))
  const scopeRef = useRef({ key: scopeKey, base: savedViews.active?.updatedAt ?? '' })
  scopeRef.current = { key: scopeKey, base: savedViews.active?.updatedAt ?? '' }
  const markMoved = savedViews.markMoved

  /* Harta asteapta sa se stie ce view se deschide: altfel s-ar desena o data
     cu filtrele implicite si inca o data cu ale view-ului. */
  const data = useQuery({ ...mapDataQuery(fetchQuery), enabled: screen.isSuccess && savedViews.settled })

  const canvas = useRef<HTMLDivElement | null>(null)
  const cyRef = useRef<Cy | null>(null)
  const timers = useRef<number[]>([])
  const viewport = useRef<{ zoom: number; pan: { x: number; y: number } } | null>(null)
  const moved = useRef(false)

  const stopPulse = useCallback(() => {
    timers.current.forEach((t) => window.clearInterval(t))
    timers.current = []
  }, [])

  const graph = data.data

  const shown = useMemo(() => (graph ? buildView(graph, grouping, graph.request.root) : null), [graph, grouping])

  useEffect(() => {
    const container = canvas.current
    if (!container || !graph) return

    let alive = true

    /* Oprita inainte de `destroy`, altfel cronometrul ramane sa bata pe o
       instanta care nu mai exista. */
    stopPulse()
    cyRef.current?.destroy()
    cyRef.current = null

    const drawnWith = layoutRef.current
    /* Pozitiile: ale view-ului deschis, altfel ultima stare a omului pe harta
       asta. Nimic din ele = asezarea automata, ca pana acum. */
    const last = activeId > 0 ? null : readLast(scopeRef.current.key)
    const saved = layoutMemo.current ?? last

    drawMap({
      container,
      graph,
      root: graph.request.root,
      view: graph.request.view,
      layout: drawnWith,
      viewport: moved.current ? viewport.current : (saved?.viewport ?? null),
      positions: saved?.positions ?? null,
      shown,
      onGroup: (key) => setGrouping((g) => toggleCollapsed(g, key)),
      onNodesMoved: (positions) => {
        if (layoutMemo.current) layoutMemo.current = { ...layoutMemo.current, positions: { ...layoutMemo.current.positions, ...positions } }
        writeLast(scopeRef.current.key, { base: scopeRef.current.base, positions })
        markMoved()
      },
      onNode: (id) => {
        window.location.href = `/app/adm/impact/${id}`
      },
      onViewportMoved: () => {
        moved.current = true
      },
      onViewportChange: (v) => {
        viewport.current = v
        /* Zoomul si pozitia omului, tinute minte - numai cand le-a miscat el,
           nu la incadrarea de dupa desen. */
        if (!moved.current) return
        window.clearTimeout(lastTimer.current)
        lastTimer.current = window.setTimeout(() => writeLast(scopeRef.current.key, { base: scopeRef.current.base, viewport: v }), 400)
      },
    })
      .then((cy) => {
        if (!alive) {
          cy.destroy()
          return
        }
        cyRef.current = cy
        /* Asezarea s-a schimbat cat se desena: se aplica acum, pe panza gata. */
        const arrangeable = cy as unknown as ArrangeCy
        if (layoutRef.current !== drawnWith && typeof arrangeable.layout === 'function') {
          rearrangeMap(arrangeable, { view: graph.request.view, arrange: layoutRef.current, root: graph.request.root })
        }
        /* Pornita dupa asezare: pana atunci nodurile inca isi schimba pozitia,
           si o pulsatie peste un layout in miscare arata a defect. */
        timers.current = startPulse(cy)
      })
      .catch(() => {
        /* Biblioteca portalului n-a putut fi adusa - lasam panza goala si
           mesajul de dedesubt, in loc sa cadem cu tot ecranul. */
      })

    setDrawn({ nodes: graph.nodes.length, edges: graph.edges.length })

    return () => {
      alive = false
      stopPulse()
      cyRef.current?.destroy()
      cyRef.current = null
    }
    /* `drawTick`: un view deschis redeseneaza si cand graful e acelasi. */
  }, [graph, shown, stopPulse, drawTick])

  /*
   * ALTA GRUPARE (sediu / retea / aplicatie), alte casete: pozitiile tinute
   * erau pentru casetele de dinainte si, puse peste cele noi, le-ar suprapune.
   * Harta se reaseaza atunci de la zero. Restrangerea si filtrele pastreaza
   * pozitiile - membrii raman unde erau, o caseta restransa se pune langa
   * vecinii ei.
   */
  const changeGrouping = (next: MapGroupState) => {
    if (next.group !== grouping.group) {
      if (layoutMemo.current) layoutMemo.current = { positions: {}, viewport: null }
      forgetLast(scopeRef.current.key)
      moved.current = false
      viewport.current = null
    }
    setGrouping(next)
  }

  /*
   * ALTA ASEZARE, ACELASI DESEN. Nodurile, culorile si haloul raman; se muta
   * doar pozitiile, si harta se incadreaza din nou - zoomul de dinainte nu mai
   * arata spre nimic. Primul desen isi ia asezarea singur, din `layoutRef`.
   */
  const arranged = useRef<Arrange>(layout)
  useEffect(() => {
    if (arranged.current === layout) return
    arranged.current = layout
    const cy = cyRef.current as unknown as ArrangeCy | null
    if (!cy || !graph || typeof cy.layout !== 'function') return
    moved.current = false
    viewport.current = null
    rearrangeMap(cy, { view: graph.request.view, arrange: layout, root: graph.request.root })
    /* Pozitiile tinute pana acum erau ale asezarii vechi: de-acum sunt cele
       noi, altfel urmatorul redesen (o caseta restransa) le-ar pune inapoi. */
    if (layoutMemo.current) layoutMemo.current = { positions: readPositions(cy), viewport: null }
    forgetLast(scopeRef.current.key)
  }, [layout, graph])

  /*
   * NUMAI CULORILE SE IMPROSPATEAZA, nu desenul.
   *
   * Harta redesenata de la zero pierde zoomul, pozitia si selectia omului -
   * adica exact ce facea cineva cand a aparut alarma. Nodurile raman aceleasi;
   * se schimba doar haloul din jurul lor.
   */
  useEffect(() => {
    if (!graph || graph.nodes.length === 0) return

    const ids = graph.nodes.map((n) => n.id)

    const tick = () => {
      const cy = cyRef.current
      if (!cy || document.hidden) return

      fetchMapHealth(ids)
        .then((payload) => {
          const live = cyRef.current
          if (!live) return
          live.batch(() => {
            for (const [id, state] of Object.entries(payload.health)) {
              const node = live.getElementById(String(id))
              if (node.length === 0) continue
              node.removeClass('health-down health-degraded pulse-on pulse-on-soft')
              if (Number(state) === HEALTH_DOWN) node.addClass('health-down')
              else if (Number(state) === HEALTH_DEGRADED) node.addClass('health-degraded')
            }
          })
        })
        .catch(() => {
          /* O improspatare picata nu strica desenul: se incearca din nou peste
             un minut. */
        })
    }

    const timer = window.setInterval(tick, HEALTH_EVERY_MS)
    return () => window.clearInterval(timer)
  }, [graph])

  /*
   * CE SE VEDEA, in cuvinte - antetul documentului.
   *
   * Compus din filtrele de pe ecran, nu din ce a raspuns serverul: hartia
   * trebuie sa spuna ce a cerut omul, ca peste sase luni sa se stie ce e in ea.
   */
  const summary = (): string => {
    const parts: string[] = []
    const rootName = graph && graph.request.root > 0 ? (screen.data?.rootName ?? '') : ''
    parts.push(rootName === '' ? 'the whole estate' : rootName)
    if (query.view) parts.push(query.view)
    if (query.direction && query.direction !== 'both') {
      parts.push(query.direction === 'out' ? 'what it uses' : 'what uses it')
    }
    if (query.show_gone === 1) parts.push('including gone')
    return parts.join(' · ')
  }

  const save = async (what: 'pdf' | 'xlsx') => {
    setExportError('')
    setExporting(what)
    try {
      if (what === 'xlsx') {
        await downloadMapXlsx(fetchQuery)
      } else {
        const cy = cyRef.current
        if (!cy) throw new Error('The map has not finished drawing yet.')
        await downloadMapPdf(mapPng(cy), summary(), Boolean(graph?.truncated))
      }
    } catch (error) {
      setExportError((error as Error).message)
    } finally {
      setExporting('')
    }
  }

  if (screen.isError) {
    const status = (screen.error as { status?: number }).status
    if (status === 403) return <Forbidden />
    return (
      <Content>
        <div className="alert alert-warning" style={{ margin: 16 }}>
          <i className="fas fa-exclamation-triangle"></i> {(screen.error as Error).message}
        </div>
      </Content>
    )
  }

  const page = screen.data
  const services = page?.services ?? []
  const roots = page?.roots ?? []
  const views = page?.views ?? []
  const busy = data.isFetching

  const change = (patch: Partial<MapState>) => setQuery((q) => ({ ...q, ...patch }))

  /*
   * DOUA RANDURI DE CONTROALE, NU TREI. Bifa „Include gone", numaratoarea
   * desenului si butoanele Redraw / Export / PDF / Excel stau pe randul al
   * doilea, langa filtrele de pe client (`MapControls`), ca harta sa inceapa
   * mai sus.
   */
  const includeGone = (
    <span className="adm-check">
      <SettingsCheck
        id="mapShowGone"
        title="Connections that were seen before but no longer appear"
        checked={query.show_gone === 1}
        onChange={(on) => change({ show_gone: on ? 1 : 0 })}
        label="Include gone"
      />
    </span>
  )

  const drawnText = <span id="mapCount">{drawn ? `${drawn.nodes} items, ${drawn.edges} connections` : ''}</span>

  const actions = (
    <span className="adm-map-actions">
      <button
        className="btn btn-default btn-sm adm-redraw"
        id="mapRefresh"
        title="Redraw"
        onClick={() => {
          /* „Redraw" inseamna chiar „arata-mi tot de la inceput", deci
             uita ce marise omul. */
          moved.current = false
          viewport.current = null
          /* Si pozitiile tinute minte: de la inceput inseamna asezarea
             automata. Un view deschis ramane pe server, neatins. */
          layoutMemo.current = null
          forgetLast(scopeKey)
          if (savedViews.active) savedViews.markMoved()
          setRedraws((n) => n + 1)
          void data.refetch()
        }}
      >
        <i className="fas fa-sync-alt"></i>
      </button>

      {/* Exportul PNG/PDF facut in browser: `map/exportMap.ts`. */}
      <MapExportButton
        getCy={() => cyRef.current}
        disabled={!graph || graph.nodes.length === 0}
        organization={shell.data?.company?.name ?? ''}
        /* Cu un view salvat deschis, titlul si fisierul poarta numele lui. */
        view={savedViews.active?.name ?? query.view ?? ''}
        subtitle={summary}
        partial={Boolean(graph?.truncated)}
        pulse={{
          pause: stopPulse,
          resume: () => {
            if (cyRef.current) timers.current = startPulse(cyRef.current)
          },
        }}
      />

      {page?.pdfAvailable ? (
        <button
          className="btn btn-default btn-sm adm-redraw"
          id="mapExportPdf"
          title="Download the map as a PDF document"
          disabled={exporting !== '' || !graph || graph.nodes.length === 0}
          onClick={() => void save('pdf')}
        >
          <i className={exporting === 'pdf' ? 'fas fa-spinner fa-spin' : 'fas fa-file-pdf'}></i>
        </button>
      ) : null}

      <button
        className="btn btn-default btn-sm adm-redraw"
        id="mapExportExcel"
        title="Download what is drawn as an Excel workbook"
        disabled={exporting !== ''}
        onClick={() => void save('xlsx')}
      >
        <i className={exporting === 'xlsx' ? 'fas fa-spinner fa-spin' : 'fas fa-file-excel'}></i>
      </button>
    </span>
  )

  return (
    <Content>
      <div id="content">
        <AdmPageHead
          tone="blue-hoki"
          icon="fa-project-diagram"
          title="ADM Map"
          note="how the estate hangs together: what talks to what, and what would fall over with it"
        />

        {/*
          BARA SE DESENEAZA CAND ARE CE PUNE IN EA.
          Optiunile - lucrurile de la care se poate porni si cele trei vederi -
          vin de pe server. Un `<select>` desenat inaintea lor n-are optiunea
          pe care o tine ecranul, deci se randeaza GOL: omul vede o bara cu
          campuri fara nimic in ele, si o alegere facuta atunci cade pe gol.
        */}
        {page ? (
        <div className="adm-map-toolbar" id="mapToolbar">
        <div className="adm-bar adm-map-row">
          <SavedViewsBar views={savedViews} />

          <div className="adm-field adm-map-root">
            <label htmlFor="mapRoot">Start from</label>
            <select
              className="form-control input-sm"
              id="mapRoot"
              value={String(query.root ?? 0)}
              onChange={(e) => change({ root: Number(e.target.value) })}
            >
              {/* Gol inseamna parcul intreg, nu „n-am ce desena". */}
              <option value="0">Everything</option>
              {services.length > 0 ? (
                <>
                  {/* Serviciile in grupul lor si sus: intrebarea pentru care
                      exista ecranul incepe de la un serviciu, iar cautarea lui
                      printre patru sute de statii e mutarea gresita. */}
                  <optgroup label="Services">
                    {services.map((item) => (
                      <option key={item.id} value={item.id}>
                        {rootLabel(item)}
                      </option>
                    ))}
                  </optgroup>
                  <optgroup label="Everything else">
                    {roots.map((item) => (
                      <option key={item.id} value={item.id}>
                        {rootLabel(item)}
                      </option>
                    ))}
                  </optgroup>
                </>
              ) : (
                roots.map((item) => (
                  <option key={item.id} value={item.id}>
                    {rootLabel(item)}
                  </option>
                ))
              )}
            </select>
          </div>

          <div className="adm-field">
            <label htmlFor="mapView">View</label>
            {/* Lista vine de pe server, ca sa nu se desparta de felurile de
                relatie pe care le filtreaza. */}
            <select className="form-control input-sm" id="mapView" value={query.view} onChange={(e) => change({ view: e.target.value })}>
              {views.map((view) => (
                <option key={view} value={view}>
                  {view.charAt(0).toUpperCase() + view.slice(1)}
                </option>
              ))}
            </select>
          </div>

          <div className="adm-field">
            <label htmlFor="mapArrange">Arrange</label>
            {/* Cum se aseaza ce s-a adus - nu ce se aduce. Schimbarea nu
                cere nimic de la server. */}
            <select
              className="form-control input-sm"
              id="mapArrange"
              value={layout}
              onChange={(e) => change({ layout: e.target.value as Arrange })}
            >
              {ARRANGES.map((value) => (
                <option key={value} value={value}>
                  {ARRANGE_LABELS[value]}
                </option>
              ))}
            </select>
          </div>

          <div className="adm-field">
            <label htmlFor="mapDirection">Direction</label>
            <select
              className="form-control input-sm"
              id="mapDirection"
              value={query.direction}
              onChange={(e) => change({ direction: e.target.value })}
            >
              <option value="both">Both ways</option>
              <option value="out">What it uses</option>
              <option value="in">What uses it</option>
            </select>
          </div>
        </div>

        {/* Gruparea are nevoie de graf; pana soseste el, randul al doilea
            tine doar ce nu depinde de desen. */}
        {graph ? (
          <MapControls state={grouping} view={shown} onChange={changeGrouping} filters={includeGone} stats={drawnText}>
            {actions}
          </MapControls>
        ) : (
          <div className="adm-bar adm-map-controls">
            {includeGone}
            <span className="adm-spacer"></span>
            <span className="adm-count adm-map-stats">{drawnText}</span>
            {actions}
          </div>
        )}
        </div>
        ) : null}

        <div className="adm-stage">
          <div id="mapCanvas" ref={canvas} key={redraws}></div>

          {graph && graph.nodes.length === 0 ? (
            <div className="adm-empty" id="mapEmpty">
              <i className="fas fa-project-diagram"></i>
              <p>Nothing to draw for these filters.</p>
            </div>
          ) : null}

          <div className={`adm-busy${busy ? ' on' : ''}`} id="mapBusy">
            <span></span>
          </div>

          {/* Legenda e singurul lucru care da inteles formelor si culorilor,
              deci sta pe ecran, nu intr-un balonas. */}
          <div className="adm-legend">
            <span className="lg-shape lg-device"></span>Device
            <span className="lg-shape lg-app"></span>Application
            <span className="lg-shape lg-itsvc"></span>IT service
            <span className="lg-shape lg-biz"></span>Business service
            <span className="lg-sep"></span>
            {/* Toate cele cinci trepte, si in cuvintele modulului: „Critical"
                pe un element de configuratie se citeste ca o alarma critica,
                cand numarul inseamna „afacerea are nevoie de asta". */}
            <span className="lg-dot" style={{ background: '#c0392b' }}></span>Critical
            <span className="lg-dot" style={{ background: '#e67e22' }}></span>Important
            <span className="lg-dot" style={{ background: '#f1c40f' }}></span>Standard
            <span className="lg-dot" style={{ background: '#95a5a6' }}></span>Minor
            <span className="lg-dot" style={{ background: '#bdc3c7' }}></span>Not set
            <span className="lg-sep"></span>
            <span className="lg-ring lg-down"></span>Down
            <span className="lg-ring lg-degraded"></span>Degraded
            <span className="lg-sep"></span>
            <span className="lg-line"></span>Seen more often
            <span className="lg-line lg-gone"></span>Stopped appearing
          </div>
        </div>

        <div id="mapWarning" className="adm-warning">
          {exportError !== '' ? (
            <div className="alert alert-warning">
              <i className="fas fa-exclamation-triangle"></i> {exportError}
            </div>
          ) : null}
          {data.isError ? (
            <div className="alert alert-warning">
              <i className="fas fa-exclamation-triangle"></i> {(data.error as Error).message}
            </div>
          ) : null}
          {graph?.truncated ? (
            <div className="alert alert-warning">
              <i className="fas fa-exclamation-triangle"></i> Partial: the walk stopped at the server budget of{' '}
              {page?.nodeBudget ?? 300} items. Narrow the filters for the rest.
            </div>
          ) : null}
        </div>
      </div>
    </Content>
  )
}
